/**
 * Vorlage „Flüssigkeit zugießen“.
 *
 * Ein Gießgefäß kippt über der Schüssel oder dem Topf, ein Strahl fließt
 * hinein und der Spiegel steigt. Wo der Strahl auftrifft, laufen Ringe
 * auseinander. Auf Wunsch rührt ein Schneebesen dabei, wie bei „unter Rühren
 * nach und nach zugießen“.
 */
import { ease, fract, lerp, segment } from '../geometry.ts';
import { STROKE } from '../palette.ts';
import type { Tone } from '../palette.ts';
import { ellipse, faded, group } from '../scene.ts';
import type { SceneNode } from '../scene.ts';
import { pourer, stream } from '../parts/kitchen.ts';
import { arcArrow, arrow } from '../parts/stage.ts';
import { TARGET_VESSELS, targetFromText, targetScene, targetVessel } from '../parts/target.ts';
import type { TargetVessel } from '../parts/target.ts';
import { place, whisk } from '../parts/utensils.ts';
import { contents } from '../parts/vessels.ts';
import { booleanValue, choice, defineTemplate, textValue } from '../template.ts';

/** Ausgussecke des Gießgefäßes. */
const LIP = { x: 156, y: 34 } as const;

/** Zeitplan. */
const TIME = {
  cupIn: [0.02, 0.1],
  tilt: [0.1, 0.2],
  pour: [0.18, 0.7],
  untilt: [0.68, 0.76],
  cupOut: [0.74, 0.82],
  reset: [0.88, 1],
} as const;

/** Füllstand vorher und nachher (0 = randvoll, 1 = Boden). */
const LEVEL = { from: 0.62, to: 0.3 } as const;

/** Flüssigkeiten. */
const LIQUIDS = { water: 'Wasser', milk: 'Milch', cream: 'Sahne', oil: 'Öl', broth: 'Brühe' } as const;
type Liquid = keyof typeof LIQUIDS;
const LIQUID_TONES: Readonly<Record<Liquid, Tone>> = {
  water: 'tint',
  milk: 'snow',
  cream: 'snow',
  oil: 'shade',
  broth: 'shade',
};

/** Ringe, die vom Auftreffpunkt auseinanderlaufen. */
function ripples(cx: number, cy: number, t: number, strength: number): SceneNode[] {
  const nodes: SceneNode[] = [];
  for (let index = 0; index < 3; index += 1) {
    const phase = fract(t * 6 + index / 3);
    const opacity = (1 - phase) * strength;
    if (opacity <= 0.05) {
      continue;
    }
    nodes.push(
      ellipse(cx, cy, 4 + phase * 22, 1.5 + phase * 5, {
        fill: 'none',
        stroke: 'ink',
        strokeWidth: STROKE.fine,
        opacity,
      }),
    );
  }
  return nodes;
}

/** Vorlage „Flüssigkeit zugießen“. */
export const mixPourTemplate = defineTemplate({
  id: 'mix.pour',
  group: 'mix',
  title: 'Flüssigkeit zugießen',
  description:
    'Die Flüssigkeit wird aus dem Gefäß in die Schüssel oder den Topf gegossen. Bei Bedarf wird dabei mit dem Schneebesen gerührt, damit nichts klumpt.',
  durationSeconds: 5,
  keywords: [
    'zugießen',
    'zugiessen',
    'dazugießen',
    'hinzugießen',
    'eingießen',
    'dazuschütten',
    'nach und nach',
    'unter rühren',
    'einlaufen lassen',
    'flüssigkeit',
    'auffüllen',
  ],
  stillTime: 0.42,
  parameters: [
    choice('liquid', 'Flüssigkeit', LIQUIDS, 'milk'),
    choice('vessel', 'Gefäß', TARGET_VESSELS, 'bowl'),
    { kind: 'boolean', name: 'stir', label: 'dabei rühren', defaultValue: false },
  ],
  inferValues(text) {
    const lower = text.toLowerCase();
    let liquid: Liquid | undefined;
    if (lower.includes('milch')) liquid = 'milk';
    else if (lower.includes('sahne')) liquid = 'cream';
    else if (/brühe|fond/.test(lower)) liquid = 'broth';
    else if (/öl\b|oel\b|öl /.test(lower)) liquid = 'oil';
    else if (lower.includes('wasser')) liquid = 'water';
    return {
      liquid,
      ...targetFromText(text),
      stir: /unter rühren|rührend|dabei rühren|ständig rühren/.test(lower) ? true : undefined,
    };
  },
  scene(values, t) {
    const kind = textValue(values, 'vessel', 'bowl') as TargetVessel;
    const tone = LIQUID_TONES[textValue(values, 'liquid', 'milk') as Liquid] ?? 'snow';
    const stirring = booleanValue(values, 'stir', false);
    const vessel = targetVessel(kind);

    const reset = ease.inOutSine(segment(t, ...TIME.reset));
    const filled = ease.inOutSine(segment(t, ...TIME.pour)) * (1 - reset);
    const flow =
      segment(t, TIME.pour[0], TIME.pour[0] + 0.04) * (1 - segment(t, TIME.pour[1] - 0.04, TIME.pour[1]));
    const tilt =
      -70 * (ease.inOutCubic(segment(t, ...TIME.tilt)) - ease.inOutCubic(segment(t, ...TIME.untilt)));
    const cup = ease.outCubic(segment(t, ...TIME.cupIn)) * (1 - segment(t, ...TIME.cupOut));

    const level = lerp(LEVEL.from, LEVEL.to, filled);
    const surfaceY = vessel.rimY + vessel.depth * level;
    const rx = vessel.widthAt(level);
    const ry = vessel.ry * (rx / vessel.rx);
    const landing = { x: vessel.cx + 22, y: surfaceY };

    // Der Schneebesen kreist zweimal je Schleife, damit Anfang und Ende passen.
    const angle = t * 2 * Math.PI * 2;
    const tool = stirring
      ? group([
          place(
            vessel.cx - 10 + Math.cos(angle) * rx * 0.4,
            surfaceY + 4 + Math.sin(angle) * ry * 0.4,
            -14 + Math.cos(angle) * 8,
            whisk(fract(t * 6)),
            0.8,
          ),
        ])
      : null;

    return targetScene(
      kind,
      vessel,
      t,
      [
        contents(vessel, level, tone, ripples(landing.x, landing.y, t, flow)),
        tool,
        // Der Strahl liegt zwischen Rück- und Vorderwand, damit er hinter dem Rand verschwindet.
        flow > 0.02
          ? stream([LIP, { x: LIP.x - 10, y: LIP.y + 22 }, { x: landing.x, y: landing.y }], 7 * flow, t, tone)
          : null,
      ],
      [faded(cup, [pourer(LIP, 30, 36, tilt, lerp(0.85, 0.12, segment(t, ...TIME.pour)), tone)])],
    );
  },
  hints(values) {
    const kind = textValue(values, 'vessel', 'bowl') as TargetVessel;
    const vessel = targetVessel(kind);
    return [
      arrow([
        { x: LIP.x + 26, y: LIP.y + 6 },
        { x: LIP.x - 2, y: LIP.y + 30 },
        { x: vessel.cx + 30, y: vessel.rimY - 6 },
      ]),
      ...(booleanValue(values, 'stir', false)
        ? [arcArrow(vessel.cx - 10, vessel.rimY + 4, 34, 8, 200, 340)]
        : []),
    ];
  },
});
